import { dt as sharedDt } from "../../../shared/themes/redesignTokens";

/**
 * Machinery module-specific design tokens.
 * Extends the shared dt with machinery card, group pill, and health indicator tokens.
 * All blue/accent references use CSS variables via the shared dt tokens.
 */
export const dt = {
  ...sharedDt,

  // -- Text hierarchy aliases for convenience --
  text: {
    primary: sharedDt.textPrimary,
    secondary: sharedDt.textSecondary,
    tertiary: sharedDt.textMuted,
  },

  // -- Border aliases --
  border: {
    light: "#EEEEEF",
  },

  // -- Machinery card tokens --
  machinery: {
    card: {
      bg: "#FFFFFF",
      border: "1px solid #EEEEEF",
      radius: "14px",
      shadow: "0 2px 8px rgba(0,33,89,0.06)",
      shadowHover: "0 8px 24px rgba(0,33,89,0.12)",
      imageSizeLg: "120px",
      imageSizeMd: "80px",
      imageSizeSm: "80px",
    },
    groupPill: {
      radius: "12px",
      py: "2px",
      px: "10px",
      fontSize: "11px",
    },
    health: {
      green: "#22C55E",
      yellow: "#F59E0B",
      red: "#EF4444",
    },
    newBadge: {
      bg: "rgba(39, 174, 96, 0.08)",
      text: "#27AE60",
      border: "rgba(39, 174, 96, 0.25)",
    },
  },

  // -- Profile page tokens --
  profile: {
    sidebarWidth: { lg: "310px", md: "240px" },
    dotGrid: {
      color: "rgba(52, 152, 219, 0.04)",
      size: "24px 24px",
    },
    breadcrumb: {
      linkColor: sharedDt.blue,
      separator: "#9CA3AF",
      currentColor: "#6B7280",
    },
    quickAction: {
      border: "#E5E7EB",
      hoverBg: sharedDt.blueSoft,
      hoverBorder: sharedDt.blue,
      hoverColor: sharedDt.blue,
    },
    tab: {
      indicator: sharedDt.blue,
      activeColor: sharedDt.blue,
      inactiveColor: "#6B7280",
    },
  },

  // -- Specs tab tokens --
  specs: {
    sectionAccent: sharedDt.blue,
    sectionAccentWidth: "3px",
    rowOdd: "#FFFFFF",
    rowEven: "#FAFBFC",
    rowPadding: "12px 16px",
    pillBg: sharedDt.blue,
    pillText: "#FFFFFF",
    pillInactiveBg: "transparent",
    pillInactiveText: "#9CA3AF",
    pillBorder: "#E5E7EB",
    pillRadius: "20px",
    countPillBg: sharedDt.blueSoft,
    countPillText: sharedDt.blue,
    // Description block tokens
    descBg: sharedDt.blueSoft,
    descAccent: sharedDt.blueHover,
    descBodyBg: "#FAFBFE",
    // Standard equipment tokens
    equipStandardBg: "rgba(39, 174, 96, 0.03)",
    equipStandardBorder: "rgba(39, 174, 96, 0.2)",
    equipStandardAccent: "#27AE60",
    // Optional equipment tokens (adjusted purple for better WCAG AA contrast)
    equipOptionalBg: "rgba(142, 68, 173, 0.03)",
    equipOptionalBorder: "rgba(142, 68, 173, 0.2)",
    equipOptionalAccent: "#7D3C98",
    // Group divider tokens (per-category accents)
    groupCore: { accent: sharedDt.blueHover, bg: sharedDt.blueSoft },
    groupSpindle: { accent: "#E67E22", bg: "rgba(230, 126, 34, 0.04)" },
    groupWorkSupport: { accent: "#16A085", bg: "rgba(22, 160, 133, 0.04)" },
    groupControl: { accent: "#8E44AD", bg: "rgba(142, 68, 173, 0.04)" },
    groupEquipment: { accent: "#27AE60", bg: "rgba(39, 174, 96, 0.04)" },
    groupOptions: { accent: "#F39C12", bg: "rgba(243, 156, 18, 0.04)" },
    // Encoding fallback indicator
    encodingBadgeBg: "rgba(243, 156, 18, 0.08)",
    encodingBadgeText: "#92400E",
    encodingBadgeBorder: "rgba(243, 156, 18, 0.3)",
  },

  // -- Service Reports tokens --
  serviceReports: {
    table: {
      headerBg: "#FAFBFC",
      headerColor: "#6B7280",
      rowOdd: "#FFFFFF",
      rowEven: "#F9FAFB",
      rowHover: "#F6F9FD",
      rowPadding: "14px 16px",
    },
    status: {
      inProgress: { bg: "#EBF5FB", text: sharedDt.blueHover, border: "#AED6F1" },
      solved: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
    },
    stats: {
      bg: "#FFFFFF",
      border: "#E5E7EB",
      totalAccent: "#6B7280",
      openAccent: sharedDt.blueHover,
      costAccent: "#F39C12",
    },
    createBtnGradient: `linear-gradient(135deg, var(--accent, #3498DB) 0%, var(--accent-hover, #2980B9) 100%)`,
    dialogBlueAccent: sharedDt.blue,
    dialogGreenAccent: "#27AE60",

    // Detail page tokens
    detail: {
      metaCardBg: "#FAFBFC",
      metaCardBorder: "#E5E7EB",
      costHeroColor: "#F39C12",
      solutionCardBg: "rgba(39, 174, 96, 0.03)",
      solutionCardBorder: "rgba(39, 174, 96, 0.2)",
      solutionAccent: "#27AE60",
      problemAccent: "#3498DB",
      dividerColor: "#E5E7EB",
      sectionLabelColor: "#9CA3AF",
      actionBarBg: "#FAFBFC",
      actionBarBorder: "#F3F4F6",
    },

    // PersonByline tokens
    byline: {
      creatorCircleBg: sharedDt.blueGlow,
      creatorCircleText: sharedDt.blueHover,
      creatorBorder: sharedDt.blue,
      solverCircleBg: "rgba(39, 174, 96, 0.1)",
      solverCircleText: "#27AE60",
      solverBorder: "#27AE60",
      labelColor: "#9CA3AF",
      roleBadgeBg: "#F4F6F9",
      roleBadgeText: "#6B7280",
    },
    // Source type badge tokens
    sourceType: {
      manual: { bg: "#F3F4F6", text: "#6B7280", border: "#D1D5DB" },
      rfs: { bg: sharedDt.blueSoft, text: sharedDt.blueHover, border: sharedDt.blueGlow },
      maintenance: { bg: "rgba(243, 156, 18, 0.08)", text: "#92400E", border: "rgba(243, 156, 18, 0.3)" },
    },
  },

  // -- Decommission tokens --
  decommissioned: {
    badge: { bg: "#F3F4F6", text: "#6B7280", border: "#D1D5DB" },
    card: {
      opacity: 0.65,
      opacityHover: 0.85,
      borderStyle: "1px dashed #D1D5DB",
      imageFilter: "grayscale(40%)",
      imageFilterHover: "grayscale(20%)",
    },
    warning: { bg: "#FEF9E7", border: "#F39C12", text: "#92400E" },
    amber: "#F39C12",
    amberDark: "#E67E22",
  },
};
